import Axios from "axios";

const inflight = new Map();
const responseCache = new Map();

let unauthorizedHandler = null;

export const setUnauthorizedHandler = (handler) => {
  unauthorizedHandler = handler;
};

const getAuthToken = () => {
  const stored = localStorage.getItem("authUser");
  if (!stored) return null;
  try {
    return JSON.parse(stored)?.AccessToken || null;
  } catch (err) {
    return null;
  }
};

const buildCacheKey = ({ method, baseURL, url, params, data, headers }) =>
  JSON.stringify({
    method,
    baseURL,
    url,
    params,
    data,
    headers,
  });

const getCachedResponse = (key) => {
  const cached = responseCache.get(key);
  if (!cached) return null;
  if (cached.expiresAt > Date.now()) return cached.response;
  responseCache.delete(key);
  return null;
};

const setCachedResponse = (key, response, ttlMs) => {
  responseCache.set(key, {
    response,
    expiresAt: Date.now() + ttlMs,
  });
};

const requestWithCache = async (key, ttlMs, requestFn) => {
  const cached = getCachedResponse(key);
  if (cached) return cached;

  const existing = inflight.get(key);
  if (existing) return existing;

  const promise = requestFn().finally(() => inflight.delete(key));
  inflight.set(key, promise);

  const res = await promise;
  if (res?.status === 200 && ttlMs) {
    setCachedResponse(key, res, ttlMs);
  }
  return res;
};

export const createApiClient = (baseURL, options = {}) => {
  const client = Axios.create({ baseURL });

  client.interceptors.request.use(
    (config) => {
      const token = getAuthToken();
      if (token) {
        config.headers = {
          ...config.headers,
          authorization: token,
        };
      }
      if (options.getExtraHeaders) {
        const extraHeaders = options.getExtraHeaders();
        if (extraHeaders && Object.keys(extraHeaders).length > 0) {
          config.headers = {
            ...config.headers,
            ...extraHeaders,
          };
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401 && unauthorizedHandler) {
        unauthorizedHandler(error.response);
      }
      return Promise.reject(error);
    }
  );

  return client;
};

export const apiRequest = async (
  client,
  { method, url, params, data, headers, cache, ttlMs }
) => {
  const config = {
    method,
    url,
    params,
    data,
    headers,
  };

  const key = buildCacheKey({
    method,
    baseURL: client.defaults.baseURL,
    url,
    params,
    data,
    headers,
  });

  const requestFn = async () => {
    try {
      const res = await client.request(config);
      return res;
    } catch (err) {
      return err?.response || { status: 0, data: null };
    }
  };

  if (cache && method.toLowerCase() === "get") {
    return requestWithCache(key, ttlMs, requestFn);
  }

  return requestFn();
};

export const createApiCaller = (client) => ({
  Get: (endPoint = "", params = {}, options = {}) =>
    apiRequest(client, {
      method: "get",
      url: endPoint,
      params,
      ...options,
    }),
  Post: (endPoint = "", data = {}, options = {}) =>
    apiRequest(client, {
      method: "post",
      url: endPoint,
      data,
      ...options,
    }),
  Put: (endPoint = "", data = {}, options = {}) =>
    apiRequest(client, {
      method: "put",
      url: endPoint,
      data,
      ...options,
    }),
  Delete: (endPoint = "", data = {}, options = {}) =>
    apiRequest(client, {
      method: "delete",
      url: endPoint,
      data,
      ...options,
    }),
});
